import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, ClipboardCheck, FileText, Inbox, Loader2, Map, Send } from "lucide-react";
import { toast } from "sonner";
import { apiRequest } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const QUEUE = [
  ["da_controllare", "Da controllare", "Elaborati dall'app, in attesa della tua revisione"],
  ["da_correggere", "Da correggere", "Inviati ma bloccati: servono dati o documenti"],
  ["in_compilazione", "In compilazione", "L'auditor sta ancora lavorando"],
  ["approvati", "Approvati", "Controllati e approvati da un revisore umano"],
];
const KIND = { dvr: "DVR", duvri: "DUVRI", pos: "POS", pimus: "PiMUS", emergenza: "Piano di emergenza" };
const STATUS_LABEL = {
  survey_draft: "In compilazione", survey_complete: "Completo", risk_analysis: "Analisi rischi",
  auto_review_1: "Revisione 1 fatta", auto_review_2: "Revisione 2 fatta", auto_review_3: "3 revisioni fatte",
  human_review: "In revisione umana", changes_requested: "Modifiche richieste", approved: "Approvato",
  ready_for_signature: "Pronto per la firma", signed: "Firmato", issued: "Emesso",
};
const MISSING = { company: "dato azienda", meta: "dato anagrafico", task: "lavorazione", plan: "planimetria PDF" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "-");

const FEATURES = [
  ["dvr", "DVR", "Valutazione dei rischi dell'azienda", false],
  ["duvri", "DUVRI", "Rischi da interferenze tra imprese", false],
  ["pos", "POS", "Piano operativo di sicurezza del cantiere", false],
  ["pimus", "PiMUS", "Ponteggio: pianta e prospetto in PDF dal CAD", true],
  ["emergenza", "Piano di emergenza", "Evacuazione: planimetria in PDF dal CAD", true],
];

function Overview() {
  return (
    <div className="space-y-4" data-testid="safety-overview">
      <div className="grid gap-3 md:grid-cols-5">
        {FEATURES.map(([k, label, text, cad]) => (
          <Card key={k}><CardContent className="space-y-1 pt-4">
            <p className="flex items-center gap-2 font-semibold"><FileText className="h-4 w-4 text-blue-900" /> {label}</p>
            <p className="text-xs text-slate-600">{text}</p>
            {cad && <Badge variant="outline" className="gap-1"><Map className="h-3 w-3" /> ARTEGO CAD NEXT</Badge>}
          </CardContent></Card>
        ))}
      </div>
      <ol className="grid gap-2 text-sm md:grid-cols-5">
        {["L'auditor, in azienda, apre il sopralluogo e compila la checklist dal tablet", "A fine sopralluogo preme «Invia al consulente»", "L'app elabora da sola: rischi, 3 revisioni automatiche, bozze dei documenti scelti",
          "Il consulente controlla in questa casella e approva o chiede modifiche", "Dopo l'approvazione: firma ed emissione al cliente"].map((t, i) => (
          <li key={i} className="rounded border bg-white p-3"><span className="mr-2 font-bold text-blue-900">{i + 1}</span>{t}</li>
        ))}
      </ol>
    </div>
  );
}

function Pipeline({ p }) {
  if (!p) return <p className="text-xs text-slate-500">Non ancora inviato.</p>;
  return (
    <div className="space-y-2 text-sm">
      <p className="flex flex-wrap items-center gap-2">
        {p.steps.length === 0 ? <span className="text-slate-500">Revisioni già eseguite</span> : p.steps.map((s) => (
          <Badge key={s.review} variant="outline">Revisione {s.review}: {s.findings} rilievi{s.blockers ? `, ${s.blockers} blocchi` : ""}</Badge>))}
        {p.counts && Object.entries(p.counts).filter(([, v]) => v).map(([k, v]) => <Badge key={k} variant="outline">{v} {k}</Badge>)}
      </p>
      {p.why?.length > 0 && <ul className="list-disc pl-5 text-rose-800">{p.why.map((w, i) => <li key={i}>{w}</li>)}</ul>}
      <div className="grid gap-1 md:grid-cols-2">
        {p.documents.map((d) => (
          <div key={d.kind} className="rounded border p-2">
            <p className="flex items-center gap-2 font-medium">
              {d.ready ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
              {KIND[d.kind] || d.kind} <Badge variant="outline">{d.generated ? `bozza: ${d.items} voci` : "non generata"}</Badge>
            </p>
            {!d.ready && <p className="text-xs text-slate-600">Manca: {d.missing.map((m) => `${MISSING[m.type] || m.type} (${m.label})`).join("; ")}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SafetyInbox() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [queue, setQueue] = useState("da_controllare");
  const load = useCallback(() => apiRequest("/safety/inbox").then(setData).catch((e) => toast.error("Errore", { description: e.message })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (data && data.counts[queue] === 0) {
      const first = QUEUE.find(([k]) => data.counts[k] > 0);
      if (first) setQueue(first[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  if (!data) return <div className="p-8"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const rows = data.surveys.filter((s) => s.queue === queue);
  return (
    <div className="space-y-6" data-testid="safety-inbox-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">ARTEGO Safety Engine</p>
          <h2 className="font-heading text-4xl font-bold text-slate-950">Sopralluoghi guidati</h2>
        </div>
        <Button onClick={() => navigate("/aziende")}><Send className="h-4 w-4" /> Nuovo sopralluogo (scegli l'azienda)</Button>
      </div>
      <Overview />
      <Alert>
        <ClipboardCheck className="h-4 w-4" />
        <AlertTitle>{data.is_auditor ? "Casella del consulente" : "I tuoi sopralluoghi"}</AlertTitle>
        <AlertDescription>
          Le bozze restano BOZZA con filigrana fino all'approvazione di un revisore umano. Le norme del motore sono controllate
          dall'AI ma non ancora validate da un tecnico abilitato; i simboli di sicurezza delle planimetrie sono in bozza.
        </AlertDescription>
      </Alert>
      <div className="flex flex-wrap gap-2">
        {QUEUE.map(([k, label]) => (
          <Button key={k} variant={queue === k ? "default" : "outline"} onClick={() => setQueue(k)} data-testid={`inbox-tab-${k}`}>
            {k === "da_controllare" && <Inbox className="h-4 w-4" />} {label} ({data.counts[k]})
          </Button>))}
      </div>
      <p className="text-sm text-slate-500">{QUEUE.find(([k]) => k === queue)[2]}</p>
      <div className="space-y-3">
        {rows.length === 0 && <Card><CardContent className="pt-6 text-sm text-slate-500">Nessun sopralluogo in questa coda.</CardContent></Card>}
        {rows.map((s) => (
          <Card key={s.id} data-testid={`inbox-row-${s.id}`}>
            <CardHeader className="pb-2"><CardTitle className="flex flex-wrap items-center justify-between gap-2 text-lg">
              <span>{s.company_name} <span className="text-sm font-normal text-slate-500">· {s.title}</span></span>
              <span className="flex flex-wrap items-center gap-2">
                {s.kinds.map((k) => <Badge key={k} variant="outline">{KIND[k] || k}</Badge>)}
                <Badge>{STATUS_LABEL[s.status] || s.status}</Badge>
              </span>
            </CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-slate-500">
                {s.submitted ? `Inviato da ${s.submitted.by_name} il ${fmt(s.submitted.at)}` : `Ultima modifica ${fmt(s.updated_at)}`}
                {s.approval ? ` · approvato da ${s.approval.by_name} il ${fmt(s.approval.at)}` : ""} · {s.tasks} lavorazioni
              </p>
              <Pipeline p={s.pipeline} />
              {s.plans.length > 0 && <p className="flex flex-wrap gap-2 text-xs">{s.plans.map((p, i) => <Badge key={i} variant="outline" className="gap-1"><Map className="h-3 w-3" /> {p.title}: {p.has_pdf ? "PDF Artego" : "senza PDF"}</Badge>)}</p>}
              <Button size="sm" onClick={() => navigate(`/aziende/${s.company_id}/sopralluogo-guidato?survey=${s.id}`)}>
                {s.queue === "da_controllare" && data.is_auditor ? "Controlla e decidi" : "Apri"} <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
